// src/components/inventory/InventoryHistoryModal.js
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Live: the ledger reloads when this item changes anywhere (WebSocket inventory_update).
//   • Real numbers: "events/day" was history.length / 30 ("simulated"); it is now the real rate
//     over the period the entries cover. Net change card added.
//   • Filters match the real change types written by the server (Restock, Issue, Return,
//     Cycle Count, Adjustment, Transfer, Initial) plus the original ones, and stock in / out.
//   • Export CSV works (the filtered rows, with officer and reason).
//   • FlowSense AI summary of the ledger: largest change, corrections, most active officer.
//   • Fixes: maxW="250px italic" (invalid), "UUID" for a numeric id, no empty state, the footer
//     no longer claims encryption.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, Table, Thead, Tbody, Tr, Th, Td, Text, Spinner, Flex, useColorModeValue,
  Badge, Box, HStack, VStack, Select, Icon, Divider, Tooltip, Avatar, Input, InputGroup,
  InputLeftElement, Stat, StatLabel, StatNumber, StatHelpText, StatArrow, SimpleGrid,
  IconButton, Menu, MenuButton, MenuList, MenuItem, Portal, Tag, TagLabel, TagLeftIcon
} from '@chakra-ui/react';
import { Alert, AlertIcon } from '@chakra-ui/react'; // --- UPDATE
import {
  MdHistory, MdFilterList, MdFileDownload, MdSearch, MdTrendingUp,
  MdTrendingDown, MdPerson, MdCalendarToday, MdInfoOutline, MdMoreVert,
  MdShield, MdCompareArrows, MdLabel
} from 'react-icons/md';
import { MdSwapHoriz, MdFactCheck, MdUndo } from 'react-icons/md'; // --- UPDATE
import { motion, AnimatePresence } from 'framer-motion';
import inventoryService from '../../services/inventoryService';
// --- UPDATE (MOW+ v2)
import useInventoryLive from '../../hooks/useInventoryLive';
import LiveIndicator from './LiveIndicator';
import FlowSenseInsightBar from './FlowSenseInsightBar';
import { summarizeHistory, historyDirection, HISTORY_TYPE_META } from './inventoryAiIntel';
import { downloadCsv, stamp } from '../../utils/inventoryCsv';
// --- END UPDATE

// Animation Components
const MotionTr = motion(Tr);
const MotionBox = motion(Box);

const InventoryHistoryModal = ({ isOpen, onClose, item }) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("All");
  // --- UPDATE
  const [direction, setDirection] = useState('all');
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // --- END UPDATE

  // --- HOOKS DECLARED AT TOP LEVEL (FIXED) ---
  const modalBg = useColorModeValue("white", "gray.800");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const secondaryText = useColorModeValue("gray.600", "gray.400");
  const hoverBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const cardShadow = useColorModeValue("xl", "0 0 20px rgba(0,0,0,0.4)");
  const accentGradient = "linear(to-r, blue.400, teal.400)";

  // --- UPDATE: live reload when this item changes anywhere
  const live = useInventoryLive(() => setReloadKey((k) => k + 1), {
    enabled: !!(isOpen && item), pollMs: 0,
    filter: (e) => e.item_id == null || String(e.item_id) === String(item?.id),
  });
  // --- END UPDATE

  useEffect(() => {
    if (isOpen && item) {
      let alive = true; // --- UPDATE
      const fetchHistory = async () => {
        if (reloadKey === 0) setIsLoading(true); // --- UPDATE: quiet live reloads
        try {
          const data = await inventoryService.getItemHistory(item.id);
          if (!alive) return; // --- UPDATE
          setHistory(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []); // --- UPDATE: { data } too
          setError(null); live.markUpdated(); // --- UPDATE
        } catch (error) {
          console.error("❌ [FlowSense] Audit Sync Error:", error);
          if (alive) setError(error?.message || 'Could not load the ledger.'); // --- UPDATE
        } finally {
          if (alive) setIsLoading(false);
        }
      };
      fetchHistory();
      return () => { alive = false; }; // --- UPDATE
    }
    return undefined;
  }, [item, isOpen, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- UPDATE: reset filters per item
  useEffect(() => { if (isOpen) { setSearchTerm(''); setFilterType('All'); setDirection('all'); setReloadKey(0); } }, [isOpen, item]);

  // Enterprise Feature: High-performance memoized filtering
  const filteredHistory = useMemo(() => {
    return history.filter(entry => {
      const searchStr = `${entry.reason} ${entry.user_name} ${entry.change_type}`.toLowerCase();
      const matchesSearch = searchStr.includes(searchTerm.toLowerCase());
      const matchesType = filterType === "All" || entry.change_type === filterType;
      const matchesDir = direction === 'all' || historyDirection(entry) === direction; // --- UPDATE
      return matchesSearch && matchesType && matchesDir;
    });
  }, [history, searchTerm, filterType, direction]);

  // Advanced Analytics: Calculation of Stock Velocity
  const analytics = useMemo(() => {
    if (!history.length) return { totalIn: 0, totalOut: 0, frequency: 0 };
    const stats = history.reduce((acc, curr) => {
      if (curr.quantity_change > 0) acc.totalIn += curr.quantity_change;
      else acc.totalOut += Math.abs(curr.quantity_change);
      return acc;
    }, { totalIn: 0, totalOut: 0 });

    // --- UPDATE: real rate over the period covered (was history.length / 30, "simulated")
    const sm = summarizeHistory(history);
    stats.frequency = sm.perDay != null ? sm.perDay.toFixed(1) : '—';
    // --- END UPDATE
    return stats;
  }, [history]);

  // --- UPDATE: FlowSense summary of the visible rows + type list from the data
  const summary = useMemo(() => summarizeHistory(filteredHistory, { itemName: item?.name }), [filteredHistory, item]);
  const typeOptions = useMemo(() => {
    const base = ['Stock In', 'Stock Out', 'Correction'];
    const seen = Array.from(new Set(history.map((h) => h.change_type).filter(Boolean)));
    return Array.from(new Set([...seen, ...base]));
  }, [history]);
  const exportCsv = () => {
    const rows = filteredHistory.map((e) => [e.timestamp ? new Date(e.timestamp).toISOString() : '', item?.name || '', item?.sku || '', e.change_type || '', e.quantity_change, e.user_name || '', e.reason || '']);
    downloadCsv(`flowsense-ledger-${item?.sku || item?.id}-${stamp()}.csv`, ['timestamp', 'item', 'sku', 'type', 'quantity_change', 'officer', 'reason'], rows);
  };
  // --- END UPDATE

  const getChangeTypeStyles = (type) => {
    switch (type) {
      case 'Stock In': return { color: 'green', icon: MdTrendingUp };
      case 'Stock Out': return { color: 'red', icon: MdTrendingDown };
      case 'Correction': return { color: 'orange', icon: MdCompareArrows };
      case 'Initial': return { color: 'blue', icon: MdHistory };
      // --- UPDATE: the types the server writes
      case 'Restock': return { color: 'green', icon: MdTrendingUp };
      case 'Issue': return { color: 'red', icon: MdTrendingDown };
      case 'Return': return { color: 'teal', icon: MdUndo };
      case 'Adjustment': return { color: 'orange', icon: MdCompareArrows };
      case 'Cycle Count': return { color: 'purple', icon: MdFactCheck };
      case 'Transfer': return { color: 'blue', icon: MdSwapHoriz };
      // --- END UPDATE
      default: return { color: HISTORY_TYPE_META[type]?.color || 'gray', icon: MdLabel };
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="6xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(12px) brightness(0.8)" />
      <ModalContent
        bg={modalBg}
        boxShadow={cardShadow}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        maxH="90vh"
      >
        <Box h="6px" bgGradient={accentGradient} borderTopRadius="2xl" />

        <ModalHeader p={6}>
          <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
            <HStack spacing={4}>
              <Box p={3} borderRadius="xl" bg="blue.500" color="white" boxShadow="0 4px 12px rgba(66, 153, 225, 0.4)">
                <Icon as={MdShield} boxSize={6} />
              </Box>
              <Box>
                <Text fontSize="2xl" fontWeight="black" letterSpacing="tight">
                  AUDIT LEDGER
                </Text>
                <HStack fontSize="sm" color={secondaryText} fontWeight="medium">
                  <Text>{item?.name}</Text>
                  <Divider orientation="vertical" h="12px" />
                  <Text color="blue.400">ID: {item?.id != null ? String(item.id) : '—'}{item?.sku ? ` · ${item.sku}` : ''}</Text>{/* --- UPDATE: was "UUID" */}
                </HStack>
              </Box>
            </HStack>
            <HStack spacing={3}>
              <LiveIndicator connected={live.connected} updatedAt={live.updatedAt} lastEvent={live.lastEvent} onRefresh={() => setReloadKey((k) => k + 1)} />{/* --- UPDATE */}
              <Button leftIcon={<MdFileDownload />} variant="outline" borderRadius="xl" size="md" onClick={exportCsv} isDisabled={!filteredHistory.length}>{/* --- UPDATE: wired */}
                Export CSV
              </Button>
              <ModalCloseButton position="static" />
            </HStack>
          </Flex>
        </ModalHeader>

        <ModalBody p={8}>
          {/* --- UPDATE: FlowSense AI */}
          <Box mb={6}>
            <FlowSenseInsightBar screen="ledger" title="Item audit ledger" data={null} compact headline={summary.lines.slice(0, 2)} extra={{ summary, item: item ? { id: item.id, name: item.name, sku: item.sku } : null }}
              rulesAnswer={() => [`${item?.name || 'Item'} — ledger summary:`, ...summary.lines].join('\n')}
              questions={['Summarise this ledger', 'Any unusual corrections?', 'Explain the inventory system for the Ministry of Works']} />
          </Box>
          {error && <Alert status="error" borderRadius="xl" mb={4}><AlertIcon />{error}<Button size="sm" ml="auto" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button></Alert>}
          {/* --- END UPDATE */}
          {/* Analytics Dashboard Section */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={10}>{/* --- UPDATE: 4 cards */}
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Stat p={5} borderRadius="2xl" bg={tableHeaderBg} borderLeft="4px solid" borderColor="green.400" boxShadow="sm">
                <StatLabel fontWeight="bold" color={secondaryText}>STOCK INGRESS</StatLabel>
                <StatNumber fontSize="3xl">+{analytics.totalIn}</StatNumber>
                <StatHelpText><StatArrow type="increase" /> Volume Increase</StatHelpText>
              </Stat>
            </MotionBox>
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Stat p={5} borderRadius="2xl" bg={tableHeaderBg} borderLeft="4px solid" borderColor="red.400" boxShadow="sm">
                <StatLabel fontWeight="bold" color={secondaryText}>STOCK EGRESS</StatLabel>
                <StatNumber fontSize="3xl">-{analytics.totalOut}</StatNumber>
                <StatHelpText><StatArrow type="decrease" /> Consumption</StatHelpText>
              </Stat>
            </MotionBox>
            {/* --- UPDATE: net */}
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <Stat p={5} borderRadius="2xl" bg={tableHeaderBg} borderLeft="4px solid" borderColor="purple.400" boxShadow="sm">
                <StatLabel fontWeight="bold" color={secondaryText}>NET CHANGE</StatLabel>
                <StatNumber fontSize="3xl">{analytics.totalIn - analytics.totalOut >= 0 ? '+' : ''}{analytics.totalIn - analytics.totalOut}</StatNumber>
                <StatHelpText>{history.length} entries</StatHelpText>
              </Stat>
            </MotionBox>
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Stat p={5} borderRadius="2xl" bg={tableHeaderBg} borderLeft="4px solid" borderColor="blue.400" boxShadow="sm">
                <StatLabel fontWeight="bold" color={secondaryText}>EVENT FREQUENCY</StatLabel>
                <StatNumber fontSize="3xl">{analytics.frequency}/day</StatNumber>
                <StatHelpText>{summarizeHistory(history).spanDays ? `Over ${summarizeHistory(history).spanDays} days` : 'System Activity Rate'}</StatHelpText>{/* --- UPDATE */}
              </Stat>
            </MotionBox>
          </SimpleGrid>

          {/* Search and Control Bar */}
          <Flex mb={6} gap={4} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
            <InputGroup flex={3}>
              <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
              <Input
                placeholder="Search by operator, action, or technical reason..."
                borderRadius="xl"
                bg={tableHeaderBg}
                border="none"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search ledger" // --- UPDATE
              />
            </InputGroup>
            <Select
              flex={1}
              borderRadius="xl"
              bg={tableHeaderBg}
              border="none"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              aria-label="Ledger type filter" // --- UPDATE
            >
              <option value="All">All Events</option>
              {/* --- UPDATE: real types from the data + the original options */}
              {typeOptions.map((t) => <option key={t} value={t}>{t === 'Correction' ? 'Corrections' : t}</option>)}
            </Select>
            {/* --- UPDATE: direction */}
            <Select flex={1} borderRadius="xl" bg={tableHeaderBg} border="none" value={direction} onChange={(e) => setDirection(e.target.value)} aria-label="Ledger direction filter">
              <option value="all">In & out</option><option value="in">Stock in only</option><option value="out">Stock out only</option>
            </Select>
          </Flex>

          {/* Main Ledger Table */}
          <Box borderRadius="2xl" borderWidth="1px" borderColor={borderColor} overflow="hidden">
            {isLoading ? (
              <Flex direction="column" align="center" justify="center" p={20}>
                <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
                <Text mt={4} fontWeight="bold" color={secondaryText}>Syncing Ledger Data...</Text>
              </Flex>
            ) : !filteredHistory.length ? ( /* --- UPDATE: empty state */
              <Flex direction="column" align="center" justify="center" p={14}>
                <Icon as={MdHistory} boxSize={10} color="gray.300" mb={3} />
                <Text color={secondaryText}>{history.length ? 'No entries match the filters.' : 'No changes recorded for this item yet.'}</Text>
              </Flex>
            ) : (
              <Table variant="simple" aria-label="Audit ledger">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th py={5}>Timestamp</Th>
                    <Th>Operator</Th>
                    <Th>Log Type</Th>
                    <Th isNumeric>Delta</Th>
                    <Th>Reason/Commentary</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <AnimatePresence>
                    {filteredHistory.map((entry, index) => {
                      const style = getChangeTypeStyles(entry.change_type);
                      return (
                        <MotionTr
                          key={entry.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: Math.min(index * 0.03, 0.6) }} // --- UPDATE: capped
                          _hover={{ bg: hoverBg }}
                        >
                          <Td>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold" fontSize="sm">
                                {new Date(entry.timestamp).toLocaleDateString()}
                              </Text>
                              <Text fontSize="xs" color={secondaryText}>
                                {new Date(entry.timestamp).toLocaleTimeString()}
                              </Text>
                            </VStack>
                          </Td>
                          <Td>
                            <HStack>
                              <Avatar size="xs" name={entry.user_name} />
                              <Text fontSize="sm" fontWeight="medium">{entry.user_name || 'system'}</Text>
                            </HStack>
                          </Td>
                          <Td>
                            <Tag size="md" colorScheme={style.color} borderRadius="full" variant="subtle">
                              <TagLeftIcon as={style.icon} />
                              <TagLabel fontWeight="bold">{entry.change_type}</TagLabel>
                            </Tag>
                          </Td>
                          <Td isNumeric>
                            <Text
                              fontWeight="black"
                              fontSize="lg"
                              color={entry.quantity_change > 0 ? 'green.400' : entry.quantity_change < 0 ? 'red.400' : secondaryText} // --- UPDATE: 0 is neutral
                            >
                              {entry.quantity_change > 0 ? `+${entry.quantity_change}` : entry.quantity_change}
                            </Text>
                          </Td>
                          <Td>
                            <Tooltip label={entry.reason} hasArrow borderRadius="lg">
                              <Text fontSize="sm" color={secondaryText} noOfLines={1} maxW="250px" fontStyle="italic">{/* --- UPDATE: was maxW="250px italic" */}
                                "{entry.reason || "Automatic system reconciliation."}"
                              </Text>
                            </Tooltip>
                          </Td>
                        </MotionTr>
                      );
                    })}
                  </AnimatePresence>
                </Tbody>
              </Table>
            )}
          </Box>
        </ModalBody>

        <ModalFooter p={6} bg={tableHeaderBg}>
          <HStack w="full" justify="space-between">
            <HStack color={secondaryText}>
              <Icon as={MdShield} />
              <Text fontSize="xs" fontWeight="bold">AUDIT LOG • FLOWSENSE MOW+ • {filteredHistory.length} OF {history.length} ENTRIES</Text>{/* --- UPDATE */}
            </HStack>
            <Button
              colorScheme="blue"
              onClick={onClose}
              px={12}
              borderRadius="xl"
              boxShadow="0 4px 12px rgba(66, 153, 225, 0.3)"
              _hover={{ transform: 'translateY(-2px)' }}
            >
              Exit Audit View
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default InventoryHistoryModal;
